import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Eye, EyeOff, Users, LayoutGrid, Shield,
  CheckCircle, AlertTriangle, LogOut, User,
  Building2, KeyRound, Loader2,
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useAuthStore } from '../../auth/store/authStore'
import { usePlatformFilterStore } from '../../../store/platformFilterStore'
import { platformPasswordResetService } from '../../../services/platformPasswordResetService'
import { platformAdminService } from '../../../services/platformAdminService'
import { peopleService } from '../../../services/peopleService'
import type { PlatformAdmin, User as UserType } from '../../../types/api.types'

// ── Types ──
type ResetType = 'org-user' | 'property-manager' | 'platform-admin'

interface TargetPreview {
  name: string
  badge: string
  badgeColor: string
  email?: string | null
  meta?: string
}

// ── Password strength ──
function getStrength(pwd: string): { bars: number; label: string; color: string } {
  if (!pwd) return { bars: 0, label: '', color: '' }
  let score = 0
  if (pwd.length >= 8) score++
  if (pwd.length >= 12) score++
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++
  else if (/[a-zA-Z]/.test(pwd)) score = Math.max(score, 1)
  if (/[0-9]/.test(pwd)) score++
  score = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4
  const map = [
    { label: '', color: '' },
    { label: 'Weak', color: 'bg-red-400' },
    { label: 'Fair', color: 'bg-amber-400' },
    { label: 'Good', color: 'bg-yellow-400' },
    { label: 'Strong', color: 'bg-brand-green' },
  ]
  return { bars: score, ...map[score] }
}

function validatePassword(pwd: string): string | undefined {
  if (!pwd) return 'Password is required'
  if (pwd.length < 8) return 'At least 8 characters required'
  if (!/[a-zA-Z]/.test(pwd)) return 'Must contain at least one letter'
  if (!/[0-9]/.test(pwd)) return 'Must contain at least one number'
  return undefined
}

const ROLE_LABELS: Record<string, string> = {
  STAFF: 'Staff', MANAGER: 'Manager', SUPER_ADMIN: 'Super Admin',
}
const ROLE_BADGE_COLOR: Record<string, string> = {
  STAFF: 'bg-gray-100 text-gray-600',
  MANAGER: 'bg-blue-100 text-blue-700',
  SUPER_ADMIN: 'bg-purple-100 text-purple-700',
}

// ── Sub-components ──

function TabButton({
  active, icon: Icon, label, onClick,
}: { active: boolean; icon: React.ElementType; label: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-[13px] font-medium transition-colors ${
        active
          ? 'bg-brand-green text-black shadow-sm'
          : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <Icon size={15} />
      {label}
    </button>
  )
}

function FieldLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return <label className="mb-1.5 block text-[13px] font-medium text-gray-700">{children}</label>
}

function SelectField({
  label, value, onChange, disabled, placeholder, children,
}: {
  label: string; value: string | number; onChange: (v: string) => void
  disabled?: boolean; placeholder?: string; children: React.ReactNode
}): JSX.Element {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none rounded-lg border border-gray-200 px-3 py-2.5 pr-9 text-[14px] text-gray-700 outline-none focus:border-brand-green disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">{placeholder ?? '— Select —'}</option>
          {children}
        </select>
        <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
      </div>
    </div>
  )
}

function TargetCard({ target }: { target: TargetPreview }): JSX.Element {
  return (
    <div className="mt-4 flex items-start gap-3 rounded-lg border border-brand-green/30 bg-brand-green/5 p-3.5">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-green/15">
        <User size={16} className="text-brand-green" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-[14px] font-semibold text-gray-900">{target.name}</p>
          <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${target.badgeColor}`}>
            {target.badge}
          </span>
        </div>
        {target.email && (
          <p className="truncate text-[12px] text-gray-500">{target.email}</p>
        )}
        {target.meta && (
          <p className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-400">
            <Building2 size={11} />
            {target.meta}
          </p>
        )}
      </div>
    </div>
  )
}

function PasswordStrengthBar({ password }: { password: string }): JSX.Element {
  const { bars, label, color } = getStrength(password)
  if (!password) return <></>
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i <= bars ? color : 'bg-gray-200'}`}
          />
        ))}
      </div>
      {label && <p className={`mt-1 text-[11px] font-medium ${bars <= 1 ? 'text-red-500' : bars <= 2 ? 'text-amber-500' : bars <= 3 ? 'text-yellow-600' : 'text-green-600'}`}>{label}</p>}
    </div>
  )
}

// ── Confirmation modal ──
function ConfirmModal({
  target,
  forceLogout,
  onConfirm,
  onCancel,
  submitting,
}: {
  target: TargetPreview
  forceLogout: boolean
  onConfirm: () => void
  onCancel: () => void
  submitting: boolean
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-amber-100">
          <KeyRound size={20} className="text-amber-600" />
        </div>
        <h2 className="text-[16px] font-bold text-gray-900">Confirm password reset</h2>
        <p className="mt-1 text-[13px] text-gray-500">This will immediately change the password for:</p>

        <TargetCard target={target} />

        <div className={`mt-3 flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] ${forceLogout ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
          <LogOut size={14} />
          {forceLogout
            ? 'Force logout ON — existing session will be invalidated immediately.'
            : 'Force logout OFF — current session stays active until expiry.'}
        </div>

        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" /> Resetting…</span>
            ) : 'Yes, reset password'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 rounded-lg border border-gray-200 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Password panel (right column) ──
function PasswordPanel({
  target,
  onSubmit,
  disabled,
  selfResetBlock,
}: {
  target: TargetPreview | null
  onSubmit: (password: string, forceLogout: boolean) => Promise<{ user_name: string; email: string | null }>
  disabled: boolean
  selfResetBlock?: string
}): JSX.Element {
  const [password, setPassword] = useState('')
  const [forceLogout, setForceLogout] = useState(true)
  const [pwdError, setPwdError] = useState<string | undefined>()
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')

  function handleInitiate() {
    const err = validatePassword(password)
    if (err) { setPwdError(err); return }
    setPwdError(undefined)
    setShowConfirm(true)
  }

  async function handleConfirm() {
    setSubmitting(true)
    try {
      const res = await onSubmit(password, forceLogout)
      setPassword('')
      setShowConfirm(false)
      setSuccessMsg(
        forceLogout
          ? 'Password reset — existing session has been invalidated.'
          : 'Password updated — current session stays active.',
      )
      toast.success(
        res.email
          ? `Password reset for '${res.user_name}'. Notification sent to ${res.email}.`
          : `Password reset for '${res.user_name}'. No email on file — share credentials manually.`,
      )
      setTimeout(() => setSuccessMsg(''), 6000)
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || (e instanceof Error ? e.message : 'Reset failed'))
      setShowConfirm(false)
    } finally {
      setSubmitting(false)
    }
  }

  const [show, setShow] = useState(false)
  const isReady = !disabled && !selfResetBlock && !!target

  return (
    <>
      <div className="flex flex-col gap-4">
        <div>
          <FieldLabel>New Password *</FieldLabel>
          <div className="relative">
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (pwdError) setPwdError(undefined); setSuccessMsg('') }}
              disabled={!isReady}
              className={`w-full rounded-lg border py-2.5 pl-3 pr-10 text-[14px] outline-none transition-colors focus:border-brand-green disabled:bg-gray-50 disabled:text-gray-400 ${pwdError ? 'border-red-400' : 'border-gray-200'}`}
              placeholder={isReady ? 'Enter new password' : 'Select a target first'}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShow((v) => !v)}
              disabled={!isReady}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {pwdError && <p className="mt-1 text-[12px] text-red-500">{pwdError}</p>}
          <PasswordStrengthBar password={password} />
        </div>

        {/* Force logout toggle */}
        <button
          type="button"
          onClick={() => setForceLogout((v) => !v)}
          disabled={!isReady}
          className={`flex items-start gap-3 rounded-lg border p-3.5 text-left transition-colors disabled:opacity-40 ${forceLogout ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
        >
          <div className="relative mt-0.5 flex-shrink-0">
            <div className={`h-5 w-9 rounded-full transition-colors ${forceLogout ? 'bg-red-500' : 'bg-gray-200'}`} />
            <div className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${forceLogout ? 'translate-x-4' : 'translate-x-0'}`} />
          </div>
          <div>
            <p className="flex items-center gap-1.5 text-[13px] font-medium text-gray-800">
              <LogOut size={13} className={forceLogout ? 'text-red-500' : 'text-gray-400'} />
              Force logout
            </p>
            <p className="mt-0.5 text-[12px] text-gray-500">
              {forceLogout
                ? 'Session invalidated immediately on the next request.'
                : "Password updates silently — user's active session stays valid."}
            </p>
          </div>
        </button>

        {/* Self-reset block */}
        {selfResetBlock && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-700">
            <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
            {selfResetBlock}
          </div>
        )}

        {/* Success banner */}
        {successMsg && (
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-[13px] text-green-700">
            <CheckCircle size={15} className="flex-shrink-0" />
            {successMsg}
          </div>
        )}

        <button
          type="button"
          onClick={handleInitiate}
          disabled={!isReady}
          className="mt-auto w-full rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-35"
        >
          Reset Password
        </button>

        {!isReady && !selfResetBlock && (
          <p className="text-center text-[12px] text-gray-400">Select a target on the left to continue</p>
        )}
      </div>

      {showConfirm && target && (
        <ConfirmModal
          target={target}
          forceLogout={forceLogout}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
          submitting={submitting}
        />
      )}
    </>
  )
}

// ── Main page ──

export function PasswordResetPage(): JSX.Element {
  const currentUser = useAuthStore((s) => s.user)
  const { organizations, allProperties } = usePlatformFilterStore()

  const [activeTab, setActiveTab] = useState<ResetType>('org-user')
  const [allUsers, setAllUsers] = useState<UserType[]>([])
  const [platformAdmins, setPlatformAdmins] = useState<PlatformAdmin[]>([])
  const [dataLoading, setDataLoading] = useState(true)

  // Panel 1
  const [p1OrgId, setP1OrgId] = useState('')
  const [p1UserId, setP1UserId] = useState('')

  // Panel 2
  const [p2OrgId, setP2OrgId] = useState('')
  const [p2PropId, setP2PropId] = useState('')

  // Panel 3
  const [p3AdminId, setP3AdminId] = useState('')

  useEffect(() => {
    async function load() {
      try {
        const [users, admins] = await Promise.all([peopleService.getAll(), platformAdminService.getAll()])
        setAllUsers(users)
        setPlatformAdmins(admins)
      } catch {
        toast.error('Could not load user list — try refreshing')
      } finally {
        setDataLoading(false)
      }
    }
    load()
  }, [])

  // Derived
  const p1Users = useMemo(
    () => (!p1OrgId ? [] : allUsers.filter((u) => String(u.organization_id) === p1OrgId)),
    [allUsers, p1OrgId],
  )
  const p1SelectedUser = useMemo(
    () => p1Users.find((u) => String(u.id) === p1UserId),
    [p1Users, p1UserId],
  )
  const p2Properties = useMemo(
    () => (!p2OrgId ? [] : allProperties.filter((p) => String(p.organization_id) === p2OrgId)),
    [allProperties, p2OrgId],
  )
  const p2SelectedProp = useMemo(
    () => p2Properties.find((p) => String(p.id) === p2PropId),
    [p2Properties, p2PropId],
  )
  const p3SelectedAdmin = useMemo(
    () => platformAdmins.find((a) => String(a.id) === p3AdminId),
    [platformAdmins, p3AdminId],
  )

  // Target previews
  const target1: TargetPreview | null = p1SelectedUser
    ? {
        name: p1SelectedUser.name,
        badge: ROLE_LABELS[p1SelectedUser.role] ?? p1SelectedUser.role,
        badgeColor: ROLE_BADGE_COLOR[p1SelectedUser.role] ?? 'bg-gray-100 text-gray-600',
        email: p1SelectedUser.email,
        meta: organizations.find((o) => o.id === p1SelectedUser.organization_id)?.name,
      }
    : null

  const target2: TargetPreview | null = p2SelectedProp
    ? {
        name: p2SelectedProp.name,
        badge: p2SelectedProp.code,
        badgeColor: 'bg-purple-100 text-purple-700',
        meta: organizations.find((o) => String(o.id) === p2OrgId)?.name,
      }
    : null

  const p3SelfBlock =
    p3AdminId && String(currentUser?.id) === p3AdminId
      ? 'Use Change Password in your profile to change your own password.'
      : undefined

  const target3: TargetPreview | null = p3SelectedAdmin
    ? {
        name: p3SelectedAdmin.name,
        badge: 'Platform Admin',
        badgeColor: 'bg-green-100 text-green-700',
        email: p3SelectedAdmin.email,
        meta: p3SelectedAdmin.organization_name ?? undefined,
      }
    : null

  const handleResetOrgUser = useCallback(async (password: string, forceLogout: boolean) => {
    if (!p1OrgId || !p1UserId) throw new Error('Select an organisation and user first')
    return platformPasswordResetService.resetOrgUser(Number(p1OrgId), Number(p1UserId), { new_password: password, force_logout: forceLogout })
  }, [p1OrgId, p1UserId])

  const handleResetPropertyAdmin = useCallback(async (password: string, forceLogout: boolean) => {
    if (!p2OrgId || !p2PropId) throw new Error('Select an organisation and property first')
    return platformPasswordResetService.resetPropertyAdmin(Number(p2OrgId), Number(p2PropId), { new_password: password, force_logout: forceLogout })
  }, [p2OrgId, p2PropId])

  const handleResetPlatformAdmin = useCallback(async (password: string, forceLogout: boolean) => {
    if (!p3AdminId) throw new Error('Select a platform admin first')
    return platformPasswordResetService.resetPlatformAdmin(Number(p3AdminId), { new_password: password, force_logout: forceLogout })
  }, [p3AdminId])

  const TABS: { id: ResetType; icon: React.ElementType; label: string }[] = [
    { id: 'org-user', icon: Users, label: 'Org User' },
    { id: 'property-manager', icon: LayoutGrid, label: 'Property Manager' },
    { id: 'platform-admin', icon: Shield, label: 'Platform Admin' },
  ]

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Password Reset</h1>
        <p className="mt-1 text-sm text-gray-500">
          Reset passwords on behalf of any user. Every action is logged to the platform audit trail.
        </p>
      </div>

      {/* Tab bar */}
      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map(({ id, icon, label }) => (
          <TabButton key={id} active={activeTab === id} icon={icon} label={label} onClick={() => setActiveTab(id)} />
        ))}
      </div>

      {/* Content */}
      <div className="grid gap-5 lg:grid-cols-2">

        {/* ── Left: entity selectors ── */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          {activeTab === 'org-user' && (
            <>
              <h3 className="mb-4 text-[14px] font-semibold text-gray-800">Select target user</h3>
              <div className="space-y-4">
                <SelectField
                  label="Organisation"
                  value={p1OrgId}
                  onChange={(v) => { setP1OrgId(v); setP1UserId('') }}
                  disabled={dataLoading}
                  placeholder={dataLoading ? 'Loading…' : 'Select Organisation'}
                >
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </SelectField>

                <SelectField
                  label={`User${p1OrgId ? ` (${p1Users.length} in org)` : ''}`}
                  value={p1UserId}
                  onChange={setP1UserId}
                  disabled={!p1OrgId || dataLoading}
                  placeholder={p1OrgId ? (p1Users.length === 0 ? 'No users in this org' : 'Select User') : 'Select organisation first'}
                >
                  {p1Users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}{u.role ? ` · ${ROLE_LABELS[u.role] ?? u.role}` : ''}{u.email ? ` — ${u.email}` : ''}
                    </option>
                  ))}
                </SelectField>

                {target1 && <TargetCard target={target1} />}
              </div>
            </>
          )}

          {activeTab === 'property-manager' && (
            <>
              <h3 className="mb-1 text-[14px] font-semibold text-gray-800">Select property</h3>
              <p className="mb-4 text-[12px] text-gray-400">
                Resets the MANAGER of the selected property. If the property has multiple managers, use the Org User tab instead.
              </p>
              <div className="space-y-4">
                <SelectField
                  label="Organisation"
                  value={p2OrgId}
                  onChange={(v) => { setP2OrgId(v); setP2PropId('') }}
                  disabled={dataLoading}
                  placeholder={dataLoading ? 'Loading…' : 'Select Organisation'}
                >
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </SelectField>

                <SelectField
                  label={`Property${p2OrgId ? ` (${p2Properties.length} found)` : ''}`}
                  value={p2PropId}
                  onChange={setP2PropId}
                  disabled={!p2OrgId || dataLoading}
                  placeholder={p2OrgId ? (p2Properties.length === 0 ? 'No properties' : 'Select Property') : 'Select organisation first'}
                >
                  {p2Properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                  ))}
                </SelectField>

                {target2 && <TargetCard target={target2} />}
              </div>
            </>
          )}

          {activeTab === 'platform-admin' && (
            <>
              <h3 className="mb-1 text-[14px] font-semibold text-gray-800">Select platform admin</h3>
              <p className="mb-4 text-[12px] text-gray-400">
                To change your own password, use Change Password in the top-right user menu.
              </p>
              <div className="space-y-4">
                <SelectField
                  label={`Platform Admin (${platformAdmins.length} total)`}
                  value={p3AdminId}
                  onChange={setP3AdminId}
                  disabled={dataLoading}
                  placeholder={dataLoading ? 'Loading…' : 'Select Platform Admin'}
                >
                  {platformAdmins.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} — {a.email}{String(currentUser?.id) === String(a.id) ? ' (you)' : ''}
                    </option>
                  ))}
                </SelectField>

                {target3 && <TargetCard target={target3} />}
              </div>
            </>
          )}
        </div>

        {/* ── Right: password form ── */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="mb-4 text-[14px] font-semibold text-gray-800">Set new password</h3>
          {activeTab === 'org-user' && (
            <PasswordPanel
              target={target1}
              onSubmit={handleResetOrgUser}
              disabled={!p1OrgId || !p1UserId}
            />
          )}
          {activeTab === 'property-manager' && (
            <PasswordPanel
              target={target2}
              onSubmit={handleResetPropertyAdmin}
              disabled={!p2OrgId || !p2PropId}
            />
          )}
          {activeTab === 'platform-admin' && (
            <PasswordPanel
              target={target3}
              onSubmit={handleResetPlatformAdmin}
              disabled={!p3AdminId}
              selfResetBlock={p3SelfBlock}
            />
          )}
        </div>

      </div>
    </div>
  )
}
